import { toLower } from 'lodash';
import React, { JSX, useEffect, useState } from 'react';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IncludeExcludeToggle } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/IncludeExcludeToggle';
import { SimpleSwitch } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/SimpleSwitch';
import { IDaypartingUI } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { DaypartingScheduleBuilder } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/DaypartingScheduleBuilder';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  Col,
  Cols,
  ISimpleOptionSelect,
  Row,
  Rows,
  spacing,
  Text,
  TKey,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { EAllowStatus } from '~/src/dash/utils/options';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const labelColWidth = 64;
const rowHeight = 40;

export interface IDaypartingBuilderProps {
  isHourOnly?: boolean;
  type?: TKey;
}

// DO NOT REUSE OR COPY/PASTE. THIS IS A CUSTOM SOLUTION COMPONENT
export const DaypartingBuilder = ({
  isHourOnly = false,
  type = 'Preset',
}: IDaypartingBuilderProps): JSX.Element => {
  const { session } = useBaseStore();
  const { object, defaultObject, maintainEditorValuesOnUnmount } = useFormBuilderStore();
  const { t } = usePublicaTranslation();

  const { DaypartingUI } = object;

  const [isEnabled, setIsEnabled] = useState<boolean>(
    (DaypartingUI as IDaypartingUI)?.Enabled || false,
  );
  const [status, setStatus] = useState<EAllowStatus>(object.DaypartingIsAllowed);
  const [isViewerTimezone, setIsViewerTimezone] = useState<boolean>(
    DaypartingUI?.EnableViewerTimezone || false,
  );

  useEffect(() => {
    if (maintainEditorValuesOnUnmount) return;
    return () => {
      object.Dayparting = defaultObject.Dayparting;
      object.DaypartingIsAllowed = defaultObject.DaypartingIsAllowed;
      object.DaypartingUI = defaultObject.DaypartingUI;
      object.DaypartingUI.Timezone = session.publisher.Timezone;
    };
  }, []);

  const handleSwitchEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEnabled(e.target.checked);
    DaypartingUI.Enabled = e.target.checked;
  };

  const handleToggleStatus = () => {
    const newStatus = status === EAllowStatus.ALLOW ? EAllowStatus.DENY : EAllowStatus.ALLOW;
    object.DaypartingIsAllowed = newStatus;
    setStatus(newStatus);
  };

  const handleSwitchTimezone = (value: string) => {
    const isViewer = value === 'viewer';
    setIsViewerTimezone(isViewer);
    DaypartingUI.EnableViewerTimezone = isViewer;
  };

  const getTimezoneOptions = (): ISimpleOptionSelect[] => {
    if (type === 'Forecast') {
      return [
        {
          key: 'publisher',
          label: 'Publisher',
          tKey: 'Publisher',
          value: 'publisher',
          isSelected: true,
        },
      ] as ISimpleOptionSelect[];
    }

    const timezoneOptions = [
      {
        key: 'bidder',
        tKey: 'Bidder',
        value: 'bidder',
        isSelected: !isViewerTimezone,
      },
      {
        key: 'viewer',
        tKey: 'Viewer',
        value: 'viewer',
        isSelected: isViewerTimezone,
      },
    ] as ISimpleOptionSelect[];

    return timezoneOptions;
  };

  const getTimezoneDescriptionTKey = (): TKey => {
    if (type === 'Forecast') {
      return 'DaypartingTimezonePublisherDescription';
    } else if (isViewerTimezone) {
      return 'DaypartingTimezoneDeviceLocationDescription';
    } else {
      return 'DaypartingTimezoneBidderDescription';
    }
  };

  return (
    <Rows gap={spacing.extraSmall} smallHorizontalPadding>
      <Row noPadding height={rowHeight}>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col width={labelColWidth}>
            <Text small tKey='Active' />
          </Col>
          <Col>
            <SimpleSwitch
              isChecked={isEnabled}
              handleSwitch={handleSwitchEnabled}
              key='Dayparting'
              inputID='dayparting_enabled_toggle'
            />
          </Col>
          <Col>
            <TooltipWithTranslation
              tKey='DaypartingTooltip'
              tProps={{ item: toLower(t(type)) }}
              placement='top'
            >
              <div style={{ display: 'flex' }}>
                <IconV2 name='info-static' color={publicaColors.gray} />
              </div>
            </TooltipWithTranslation>
          </Col>
        </Cols>
      </Row>
      {isEnabled && (
        <Row noPadding>
          <Rows>
            <Row noPadding height={rowHeight}>
              <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                <Col width={labelColWidth}>
                  <Text small tKey='Logic' />
                </Col>
                <Col>
                  <IncludeExcludeToggle
                    status={status}
                    handleToggleStatus={handleToggleStatus}
                    key={'DaypartingIsAllowed'}
                  />
                </Col>
                <Col flex style={{ textAlign: 'right' }}>
                  <Text
                    extraSmall
                    italic
                    tKey={
                      status === EAllowStatus.ALLOW
                        ? 'DaypartingIncludeDescription'
                        : 'DaypartingExcludeDescription'
                    }
                    tProps={{ item: t(type) }}
                  />
                </Col>
              </Cols>
            </Row>
            <Row noPadding height={rowHeight}>
              <Cols gap={spacing.base} style={{ alignItems: 'center', height: `${rowHeight}px` }}>
                <Col width={labelColWidth}>
                  <Text small tKey='Timezone' />
                </Col>
                <Col>
                  <PillSwitcherV2
                    size='small'
                    options={getTimezoneOptions()}
                    onChange={handleSwitchTimezone}
                  />
                </Col>
                <Col flex style={{ textAlign: 'right' }}>
                  <Text extraSmall italic tKey={getTimezoneDescriptionTKey()} />
                </Col>
              </Cols>
            </Row>
            <Row smallVerticalPadding noHorizontalPadding style={{ minHeight: `${rowHeight}px` }}>
              <Cols
                gap={spacing.base}
                style={{ alignItems: 'flex-start', minHeight: `${rowHeight}px` }}
              >
                <Col paddingTop={spacing.extraSmall} width={labelColWidth}>
                  <Text small tKey='Period' />
                </Col>
                <Col flex>
                  <DaypartingScheduleBuilder
                    initialPeriods={(DaypartingUI as IDaypartingUI).Periods}
                    isHourOnly={isHourOnly}
                  />
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Row>
      )}
    </Rows>
  );
};
